import { Fragment, useState, useEffect, useMemo, useRef } from 'react';
import { NavLink } from 'react-router';
import api, { BROWSER_TZ } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { PageBanner } from '@/components/PageBanner';
import { PageHeader } from '@/components/PageHeader';
import { PageSkeleton } from '@/components/PageSkeleton';
import { RegionalMapView } from '@/components/RegionalMapView';
import { KpiCard as StatCard } from '@/components/KpiCard';
import { DetailTableRangeTabs, TrendRangeTabs } from '@/components/TrendRangeTabs';
import { DataTablePagination, DataTableSortHeader, DataTableToolbar } from '@/components/DataTableControls';
import {
  chartTooltipStyle,
  commonCartesianGridProps,
  commonXAxisProps,
  commonYAxisProps,
  formatTrendTick,
  formatTrendTooltipLabel,
  getTrendXAxisInterval,
} from '@/lib/chartTheme';
import { AnalyticsTabBar } from '@/components/AnalyticsTabBar';
import { ViewModeToggle } from '@/components/ViewModeToggle';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getTopicIconComponent } from '@/lib/topicIcons';
import {
  MessageSquare,
  CheckCircle,
  Clock,
  RotateCcw,
  TrendingUp,
  AlertTriangle,
  Globe,
  ArrowRight,
  HelpCircle,
	  XCircle,
	  Zap,
	  ListChecks,
	  ChevronDown,
	  Loader2,
	} from 'lucide-react';
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { EmptyState } from '@/components/EmptyState';

// Regional comparison table
function RegionalTable({ data }) {
  const rows = useMemo(() => data || [], [data]);
  const [searchQuery, setSearchQuery] = useState('');
  const [regionFilter, setRegionFilter] = useState('all');
  const [sortKey, setSortKey] = useState('conversations');
  const [sortDirection, setSortDirection] = useState('desc');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const pageSizeOptions = [5, 10, 25];

  const regionOptions = useMemo(() => (
    [...new Set(rows.map((row) => row.region).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
  ), [rows]);

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesSearch = !query || [
        row.region,
        row.conversations,
        row.avg_turns,
        row.active_users,
      ].some((value) => String(value ?? '').toLowerCase().includes(query));
      const matchesRegion = regionFilter === 'all' || row.region === regionFilter;
      return matchesSearch && matchesRegion;
    });
  }, [rows, searchQuery, regionFilter]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows].sort((a, b) => {
      if (sortKey === 'region') {
        return (a.region || '').localeCompare(b.region || '');
      }
      return Number(a[sortKey] || 0) - Number(b[sortKey] || 0);
    });
    return sortDirection === 'asc' ? sorted : sorted.reverse();
  }, [filteredRows, sortKey, sortDirection]);

  const totalPages = Math.max(Math.ceil(sortedRows.length / pageSize), 1);
  const safePage = Math.min(page, totalPages);
  const pagedRows = sortedRows.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDirection(key === 'region' ? 'asc' : 'desc');
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleRegionChange = (value) => {
    setRegionFilter(value);
    setPage(1);
  };

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Globe}
        title="No regional activity yet"
        description="Regional conversation activity will appear once employee conversations include location data."
        variant="panel"
        compact
        className="bg-secondary/30"
      />
    );
  }

  return (
    <div className="space-y-4">
      <DataTableToolbar
        searchValue={searchQuery}
        onSearchChange={handleSearchChange}
        searchPlaceholder="Search regions..."
      >
        <Select value={regionFilter} onValueChange={handleRegionChange}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Region" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All regions</SelectItem>
            {regionOptions.map((region) => (
              <SelectItem key={region} value={region}>{region}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </DataTableToolbar>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[680px] data-table">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="py-3 pl-4 pr-3 text-left">
                <DataTableSortHeader
                  label="Region"
                  sortKey="region"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
              </th>
              <th className="py-3 px-3 text-center">
                <DataTableSortHeader
                  label="Conversations"
                  sortKey="conversations"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="center"
                  className="mx-auto"
                />
              </th>
              <th className="py-3 px-3 text-center">
                <DataTableSortHeader
                  label="Avg Turns"
                  sortKey="avg_turns"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="center"
                  className="mx-auto"
                />
              </th>
              <th className="py-3 pl-3 pr-4 text-center">
                <DataTableSortHeader
                  label="Active Users"
                  sortKey="active_users"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="center"
                  className="mx-auto"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((row) => (
              <tr key={row.region} className="border-b border-border transition-colors last:border-b-0 hover:bg-secondary/50">
                <td className="py-3 pl-4 pr-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <Globe className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                    <span className="font-medium text-foreground truncate">{row.region}</span>
                  </div>
                </td>
                <td className="py-3 px-3 text-center font-medium tabular-nums text-foreground">
                  {Number(row.conversations || 0).toLocaleString()}
                </td>
                <td className="py-3 px-3 text-center tabular-nums text-muted-foreground">
                  {Number(row.avg_turns || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                </td>
                <td className="py-3 pl-3 pr-4 text-center tabular-nums text-muted-foreground">
                  {Number(row.active_users || 0).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pagedRows.length === 0 && (
          <EmptyState
            icon={Globe}
            title="No matching regions"
            description="Adjust search or filters to see more regional activity."
            variant="plain"
            compact
          />
        )}
      </div>

      <DataTablePagination
        page={safePage}
        totalPages={totalPages}
        totalItems={sortedRows.length}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        onPageChange={setPage}
        onPageSizeChange={(value) => {
          setPageSize(value);
          setPage(1);
        }}
        itemLabel="regions"
        pageSizeTestId="regional-activity-page-size"
      />
    </div>
  );
}

// Conversation flow visualization
function ConversationFlow({ data, loading = false }) {
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  const flows = [
    { key: 'direct_answer', label: 'Direct Answer', sublabel: '1-2 turns', color: '#10b981', icon: Zap },
    { key: 'clarification_needed', label: 'Clarification', sublabel: '3-4 turns', color: '#3b82f6', icon: HelpCircle },
    { key: 'deep_exploration', label: 'Deep Exploration', sublabel: '4+ turns', color: '#8b5cf6', icon: MessageSquare },
    { key: 'escalated', label: 'Escalated', sublabel: 'Human help', color: '#f43f5e', icon: AlertTriangle },
    { key: 'abandoned', label: 'Abandoned', sublabel: 'Left before completion', color: '#94a3b8', icon: XCircle },
  ];

  if (loading) {
    return (
      <div className="space-y-4" data-testid="conversation-flow-loading">
        {flows.map((flow) => (
          <Skeleton key={flow.key} className="h-14 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {flows.map((flow) => {
        const value = data[flow.key] || 0;
        const pct = total > 0 ? Math.round(value / total * 100) : 0;
        const Icon = flow.icon;
        return (
          <div key={flow.key} className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                 style={{ backgroundColor: `${flow.color}15` }}>
              <Icon className="w-5 h-5" style={{ color: flow.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-foreground">{flow.label}</span>
                <span className="text-sm font-bold" style={{ color: flow.color }}>{pct}%</span>
              </div>
              <Progress value={pct} className="h-2" style={{ '--progress-color': flow.color }} />
              <p className="text-xs text-muted-foreground mt-1">{flow.sublabel}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatSummaryDate(value) {
  if (!value) return 'Unknown date';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value).slice(0, 16);
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Topic complexity table
function TopicComplexityTable({ data, window = '7d' }) {
  const rows = useMemo(() => data || [], [data]);
  const [searchQuery, setSearchQuery] = useState('');
  const [complexityFilter, setComplexityFilter] = useState('all');
  const [sortKey, setSortKey] = useState('conversation_count');
  const [sortDirection, setSortDirection] = useState('desc');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [expandedTopicId, setExpandedTopicId] = useState(null);
  const [summaryCache, setSummaryCache] = useState({});
  const [summaryPages, setSummaryPages] = useState({});
  const [summaryPageSize, setSummaryPageSize] = useState(10);
  const [loadingTopicId, setLoadingTopicId] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const pageSizeOptions = [5, 10, 25];
  const summaryPageSizeOptions = [5, 10, 25, 50];

  useEffect(() => {
    setExpandedTopicId(null);
    setSummaryCache({});
    setSummaryPages({});
    setSummaryError(null);
    setLoadingTopicId(null);
  }, [window]);

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesSearch = !query || (row.topic_name || '').toLowerCase().includes(query);
      const matchesComplexity = complexityFilter === 'all'
        || (complexityFilter === 'high' && Number(row.avg_turns || 0) > 4)
        || (complexityFilter === 'standard' && Number(row.avg_turns || 0) <= 4);
      return matchesSearch && matchesComplexity;
    });
  }, [rows, searchQuery, complexityFilter]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows].sort((a, b) => {
      if (sortKey === 'topic_name') {
        return (a.topic_name || '').localeCompare(b.topic_name || '');
      }
      return Number(a[sortKey] || 0) - Number(b[sortKey] || 0);
    });
    return sortDirection === 'asc' ? sorted : sorted.reverse();
  }, [filteredRows, sortKey, sortDirection]);

  const totalPages = Math.max(Math.ceil(sortedRows.length / pageSize), 1);
  const safePage = Math.min(page, totalPages);
  const pagedRows = sortedRows.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDirection(key === 'topic_name' ? 'asc' : 'desc');
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleComplexityChange = (value) => {
    setComplexityFilter(value);
    setPage(1);
  };

  const getSummaryCacheKey = (topicId, summaryPage = 1, size = summaryPageSize) => (
    `${topicId}:${window}:${summaryPage}:${size}`
  );

  const fetchTopicSummaries = async (row, summaryPage = 1, size = summaryPageSize) => {
    if (!row.topic_id) return;

    const cacheKey = getSummaryCacheKey(row.topic_id, summaryPage, size);
    // Clear any stale error before the cache-hit short-circuit so a prior
    // failed load on another topic doesn't render over valid cached data.
    setSummaryError(null);
    if (summaryCache[cacheKey]) return;

    setLoadingTopicId(row.topic_id);
    try {
      const response = await api.get(`/analytics/conversations/topics/${encodeURIComponent(row.topic_id)}/summaries?window=${window}&page=${summaryPage}&page_size=${size}`);
      setSummaryCache((current) => ({ ...current, [cacheKey]: response.data }));
    } catch (err) {
      console.error('Failed to fetch topic summaries:', err);
      setSummaryError('Unable to load summaries for this topic.');
    } finally {
      setLoadingTopicId(null);
    }
  };

  const handleToggleSummaries = async (row) => {
    if (!row.topic_id) return;

    if (expandedTopicId === row.topic_id) {
      setExpandedTopicId(null);
      return;
    }

    setExpandedTopicId(row.topic_id);
    const nextPage = summaryPages[row.topic_id] || 1;
    await fetchTopicSummaries(row, nextPage, summaryPageSize);
  };

  const handleSummaryPageChange = async (row, nextPage) => {
    setSummaryPages((current) => ({ ...current, [row.topic_id]: nextPage }));
    await fetchTopicSummaries(row, nextPage, summaryPageSize);
  };

  const handleSummaryPageSizeChange = async (row, nextPageSize) => {
    setSummaryPageSize(nextPageSize);
    // Page size is shared across topics, so every stored per-topic page index
    // is stale under the new size — reset them all to avoid an out-of-range
    // page (empty panel + "Page N of M" overflow) when another topic is opened.
    setSummaryPages({ [row.topic_id]: 1 });
    await fetchTopicSummaries(row, 1, nextPageSize);
  };

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={MessageSquare}
        title="No discussed topics yet"
        description="Topics will appear once employee conversations are classified."
        variant="panel"
        compact
        className="bg-secondary/30"
      />
    );
  }

  return (
    <div className="space-y-4">
      <DataTableToolbar
        searchValue={searchQuery}
        onSearchChange={handleSearchChange}
        searchPlaceholder="Search topics..."
      >
        <Select value={complexityFilter} onValueChange={handleComplexityChange}>
          <SelectTrigger className="w-full sm:w-44">
            <SelectValue placeholder="Complexity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All complexity</SelectItem>
            <SelectItem value="high">High avg turns</SelectItem>
            <SelectItem value="standard">Standard avg turns</SelectItem>
          </SelectContent>
        </Select>
      </DataTableToolbar>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[560px] data-table">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="py-3 pl-4 pr-3 text-left">
                <DataTableSortHeader
                  label="Topic"
                  sortKey="topic_name"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
              </th>
              <th className="py-3 px-3 text-center">
                <DataTableSortHeader
                  label="Conversations"
                  sortKey="conversation_count"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="center"
                  className="mx-auto"
                />
              </th>
              <th className="py-3 pl-3 pr-4 text-center">
                <DataTableSortHeader
                  label="Avg Turns"
                  sortKey="avg_turns"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="center"
                  className="mx-auto"
                />
              </th>
            </tr>
          </thead>
          <tbody>
	            {pagedRows.map((row) => {
	              const TopicIcon = getTopicIconComponent(row.icon);
	              const summaryPage = summaryPages[row.topic_id] || 1;
	              const cacheKey = getSummaryCacheKey(row.topic_id, summaryPage, summaryPageSize);
	              const summaryData = summaryCache[cacheKey];
	              const isExpanded = expandedTopicId === row.topic_id;
	              const rawSummaries = summaryData?.summaries || [];
	              const paginationMeta = summaryData?.pagination || {};
	              const summaryTotalItems = Number(paginationMeta.total_items || summaryData?.summary_count || rawSummaries.length || 0);
	              const summaryCurrentPageSize = Number(paginationMeta.page_size || summaryPageSize);
	              const summaryCurrentPage = Number(paginationMeta.page || summaryPage);
	              const summaryTotalPages = Math.max(
	                Number(paginationMeta.total_pages || 0),
	                Math.ceil(summaryTotalItems / summaryCurrentPageSize),
	                1
	              );
	              const shouldPageClientSide = rawSummaries.length > summaryCurrentPageSize;
	              const visibleSummaries = shouldPageClientSide
	                ? rawSummaries.slice((summaryCurrentPage - 1) * summaryCurrentPageSize, summaryCurrentPage * summaryCurrentPageSize)
	                : rawSummaries;
	              const summaryPagination = {
	                page: summaryCurrentPage,
	                page_size: summaryCurrentPageSize,
	                total_pages: summaryTotalPages,
	                total_items: summaryTotalItems,
	              };
	              const displayedConversationCount = summaryData
	                ? summaryTotalItems
	                : Number(row.conversation_count || 0);
	              const displayedAvgTurns = summaryData && summaryData.avg_turns !== undefined
	                ? Number(summaryData.avg_turns || 0)
	                : Number(row.avg_turns || 0);
	              return (
	                <Fragment key={row.topic_id || row.topic_name}>
	                  <tr className="border-b border-border transition-colors last:border-b-0 hover:bg-secondary/50">
	                    <td className="py-3 pl-4 pr-3">
	                      <button
	                        type="button"
	                        className="flex min-w-0 items-center gap-2 text-left"
	                        onClick={() => handleToggleSummaries(row)}
	                        aria-expanded={isExpanded}
	                        aria-label={`View conversation summaries for ${row.topic_name}`}
	                      >
	                        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} aria-hidden="true" />
	                        <span
	                          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-secondary text-muted-foreground"
	                          style={{ color: row.color || undefined }}
	                        >
	                          <TopicIcon className="h-4 w-4" aria-hidden="true" />
	                        </span>
	                        <span className="font-medium text-foreground truncate">
	                          {row.topic_name}
	                        </span>
	                      </button>
	                    </td>
	                    <td className="py-3 px-3 text-center font-medium tabular-nums text-foreground">
	                      {displayedConversationCount.toLocaleString()}
	                    </td>
	                    <td className="py-3 pl-3 pr-4 text-center tabular-nums">
	                      <span className={displayedAvgTurns > 4 ? 'font-medium text-warning' : 'text-muted-foreground'}>
	                        {displayedAvgTurns.toLocaleString(undefined, { maximumFractionDigits: 1 })}
	                      </span>
	                    </td>
	                  </tr>
	                  {isExpanded && (
	                    <tr className="border-b border-border bg-secondary/20">
	                      <td colSpan={3} className="px-4 py-4">
	                        {loadingTopicId === row.topic_id && (
	                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
	                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
	                            Loading confidential summaries...
	                          </div>
	                        )}
	                        {summaryError && loadingTopicId !== row.topic_id && (
	                          <div className="text-sm text-destructive">{summaryError}</div>
	                        )}
	                        {summaryData && (
	                          <div className="space-y-3">
	                            <div className="flex flex-wrap items-center justify-between gap-2">
	                              <div>
	                                <p className="text-sm font-semibold text-foreground">
	                                  Showing {visibleSummaries.length} of {summaryTotalItems.toLocaleString()} conversation summaries
	                                </p>
	                                <p className="text-xs text-muted-foreground">
	                                  User names, emails, IDs, and raw session IDs are excluded.
	                                </p>
	                              </div>
	                              {Number(summaryData.content_gaps?.count || 0) > 0 && (
	                                <NavLink
	                                  to={`/kb/gaps?topic=${encodeURIComponent(summaryData.topic_name || row.topic_name || '')}`}
	                                  className="inline-flex items-center gap-1.5 rounded-lg border border-warning/30 bg-warning/10 px-3 py-1.5 text-xs font-medium text-warning transition-colors hover:bg-warning/15"
	                                >
	                                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
	                                  {Number(summaryData.content_gaps.open_count || 0).toLocaleString()} open knowledge gaps
	                                </NavLink>
	                              )}
	                            </div>
	                            {visibleSummaries.length > 0 ? (
	                              <div className="space-y-2">
	                                {visibleSummaries.map((summary) => {
	                                  const contextValues = [summary.department, summary.region, summary.country]
	                                    .filter((value) => value && String(value).trim().toLowerCase() !== 'unknown');
	                                  return (
	                                    <div key={summary.conversation_ref} className="rounded-lg border border-border bg-card p-3">
	                                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
	                                        <span className="font-medium text-foreground">{summary.conversation_ref}</span>
	                                        <span>{formatSummaryDate(summary.started_at)}</span>
	                                        <span>{Number(summary.message_count || 0).toLocaleString()} turns</span>
	                                        {contextValues.map((value) => (
	                                          <span key={value}>{value}</span>
	                                        ))}
	                                      </div>
	                                      <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">{summary.summary}</p>
	                                    </div>
	                                  );
	                                })}
	                              </div>
	                            ) : (
	                              <EmptyState
	                                icon={MessageSquare}
	                                title="No summaries for this topic"
	                                description="Conversation summaries will appear here once classified sessions are summarized."
	                                variant="plain"
	                                compact
	                              />
	                            )}
	                            {Number(summaryPagination.total_items || 0) > 0 && (
	                              <DataTablePagination
	                                page={summaryPagination.page}
	                                totalPages={summaryPagination.total_pages}
	                                totalItems={summaryPagination.total_items}
	                                pageSize={summaryPagination.page_size}
	                                pageSizeOptions={summaryPageSizeOptions}
	                                onPageChange={(nextPage) => handleSummaryPageChange(row, nextPage)}
	                                onPageSizeChange={(nextPageSize) => handleSummaryPageSizeChange(row, nextPageSize)}
	                                itemLabel="summaries"
	                                pageSizeTestId={`topic-${row.topic_id}-summary-page-size`}
	                                className="pt-3"
	                              />
	                            )}
	                          </div>
	                        )}
	                      </td>
	                    </tr>
	                  )}
	                </Fragment>
	              );
	            })}
          </tbody>
        </table>
        {pagedRows.length === 0 && (
          <EmptyState
            icon={MessageSquare}
            title="No matching topics"
            description="Adjust search or filters to see more topics."
            variant="plain"
            compact
          />
        )}
      </div>

      <DataTablePagination
        page={safePage}
        totalPages={totalPages}
        totalItems={sortedRows.length}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        onPageChange={setPage}
        onPageSizeChange={(value) => {
          setPageSize(value);
          setPage(1);
        }}
        itemLabel="topics"
        pageSizeTestId="topic-complexity-page-size"
      />
    </div>
  );
}

function TopicComplexityCards({ data }) {
  if (!data || data.length === 0) {
    return <TopicComplexityTable data={data} />;
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {data.slice(0, 6).map((row) => {
        const TopicIcon = getTopicIconComponent(row.icon);
        return (
          <div key={row.topic_id} className="rounded-xl border border-border bg-card p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-secondary text-muted-foreground"
                style={{ color: row.color || undefined }}
              >
                <TopicIcon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-foreground">{row.topic_name}</p>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Conversations</p>
                    <p className="font-medium text-foreground">{row.conversation_count}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Avg turns</p>
                    <p className={row.avg_turns > 4 ? 'font-medium text-warning' : 'font-medium text-foreground'}>{row.avg_turns}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ConversationsPage() {
  const { hasRole } = useAuth();
  const canManageTopics = hasRole(['admin', 'approver']);
  const [data, setData] = useState(null);
  const [flowData, setFlowData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flowLoading, setFlowLoading] = useState(true);
  const [trendLoading, setTrendLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(false);
  const [flowWindow, setFlowWindow] = useState('7d');
  const [detailWindow, setDetailWindow] = useState('7d');
  const [trendWindow, setTrendWindow] = useState('7d');
  const [mapWindow, setMapWindow] = useState('7d');
  const [activeTab, setActiveTab] = useState('data');
  const [topicViewMode, setTopicViewMode] = useState('list');
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!hasLoadedRef.current) {
        setLoading(true);
      }
      try {
        const result = await api.get(`/analytics/conversations?window=${detailWindow}&tz=${BROWSER_TZ}`);
        setData(result.data);
      } catch (err) {
        console.error('Failed to fetch conversation data:', err);
      } finally {
        hasLoadedRef.current = true;
        setLoading(false);
      }
    };
    fetchData();
  }, [detailWindow]);

  useEffect(() => {
    let cancelled = false;
    const fetchFlowData = async () => {
      setFlowLoading(true);
      try {
        const result = await api.get(`/analytics/conversations?window=${flowWindow}&tz=${BROWSER_TZ}`);
        if (cancelled) return;
        setFlowData(result.data);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch conversation flow data:', err);
      }
      setFlowLoading(false);
    };

    fetchFlowData();
    return () => {
      cancelled = true;
    };
  }, [flowWindow]);

  useEffect(() => {
    let cancelled = false;
    const fetchTrendData = async () => {
      setTrendLoading(true);
      try {
        const result = await api.get(`/analytics/conversations?window=${trendWindow}&tz=${BROWSER_TZ}`);
        if (cancelled) return;
        setTrendData(result.data);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch conversation trend data:', err);
      }
      setTrendLoading(false);
    };

    fetchTrendData();
    return () => {
      cancelled = true;
    };
  }, [trendWindow]);

  useEffect(() => {
    let cancelled = false;
    const fetchMapData = async () => {
      if (activeTab !== 'geospatial') return;

      setMapLoading(true);
      try {
        const result = await api.get(`/analytics/conversations?window=${mapWindow}&tz=${BROWSER_TZ}`);
        if (cancelled) return;
        setMapData(result.data);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch conversation map data:', err);
        setMapData(null);
      }
      setMapLoading(false);
    };

    fetchMapData();
    return () => {
      cancelled = true;
    };
  }, [activeTab, mapWindow]);

  if (loading) {
    return (
      <PageSkeleton
        maxWidth="6xl"
        banner
        bannerPageKey="conversations-activity"
        kpis={4}
        tabs
        sections={2}
        sectionClassName="h-80"
        data-testid="activity-page-loading"
      />
    );
  }

  const {
    executive_snapshot,
    regional_breakdown,
    country_breakdown,
    conversation_flow,
    topic_complexity,
    trend_data,
  } = data || {};
  const flow = flowData?.conversation_flow || conversation_flow || {};
  const chartTrendData = trendData?.trend_data || trend_data || [];
  const mapRegionalBreakdown = mapData?.regional_breakdown || regional_breakdown || [];
  const mapCountryBreakdown = mapData?.country_breakdown || country_breakdown || [];

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-4 sm:px-6 lg:px-8 animate-fade-in" data-testid="activity-page">
      {/* Page Banner */}
      <PageBanner pageKey="conversations-activity" />
      
      <PageHeader title="Activity" description="Conversation patterns and engagement metrics" />

      {/* Executive Snapshot */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Conversations"
          value={executive_snapshot?.total_conversations?.toLocaleString() || '0'}
          subtitle="Total in period"
          icon={MessageSquare}
          color="blue"
        />
        <StatCard
          title="Retrieval Rate"
          value={`${Math.round(executive_snapshot?.retrieval_rate || 0)}%`}
          subtitle="Conversations with KB sources"
          icon={CheckCircle}
          color="emerald"
        />
        <StatCard
          title="Avg Turns"
          value={executive_snapshot?.avg_turns || 0}
          subtitle="Turns per conversation"
          icon={RotateCcw}
          color="purple"
        />
        <StatCard
          title="Avg Duration"
          value={`${executive_snapshot?.avg_duration_seconds || 0}s`}
          subtitle="Time per conversation"
          icon={Clock}
          color="amber"
        />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <AnalyticsTabBar
          tabs={[
            { value: 'data', label: 'Details', icon: ListChecks },
            { value: 'trends', label: 'Trends', icon: TrendingUp },
            { value: 'geospatial', label: 'World', icon: Globe },
          ]}
        />

        <TabsContent value="data" className="space-y-6">
          <Card className="bg-card border-border">
            <CardHeader className="gap-3 pb-2 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <ArrowRight className="w-5 h-5 text-success" />
                  How Employees Get Answers
                </CardTitle>
                <CardDescription>Conversation outcomes by depth</CardDescription>
              </div>
              <DetailTableRangeTabs value={flowWindow} onChange={setFlowWindow} className="w-full lg:w-auto lg:min-w-[360px]" />
            </CardHeader>
            <CardContent>
              <ConversationFlow data={flow} loading={flowLoading} />
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="gap-3 pb-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:flex-1">
                <div>
                  <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-info" />
                    Most Discussed Topics
                  </CardTitle>
                  <CardDescription>Topics with most conversation activity</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  {canManageTopics && (
                    <NavLink to="/kb/topics" className="text-sm text-info  hover:underline">
                      Manage
                    </NavLink>
                  )}
                  <ViewModeToggle value={topicViewMode} onChange={setTopicViewMode} />
                </div>
              </div>
              <DetailTableRangeTabs value={detailWindow} onChange={setDetailWindow} className="w-full lg:w-auto lg:min-w-[360px]" />
            </CardHeader>
            <CardContent>
	              {topicViewMode === 'card' ? (
	                <TopicComplexityCards data={topic_complexity || []} />
	              ) : (
	                <TopicComplexityTable data={topic_complexity || []} window={detailWindow} />
	              )}
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Globe className="w-5 h-5 text-info" />
                Regional Activity
              </CardTitle>
              <CardDescription>Conversation distribution by region</CardDescription>
            </CardHeader>
            <CardContent>
              <RegionalTable data={regional_breakdown || []} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends">
          <Card className="bg-card border-border">
            <CardHeader className="gap-3 pb-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-indigo-500" />
                  Conversation Trend
                </CardTitle>
                <CardDescription>Conversation volume over time</CardDescription>
              </div>
              <TrendRangeTabs value={trendWindow} onChange={setTrendWindow} className="w-full sm:w-auto sm:min-w-[320px]" />
            </CardHeader>
            <CardContent>
              {trendLoading ? (
                <Skeleton className="h-72 w-full rounded-xl" data-testid="conversations-trend-loading" />
              ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartTrendData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid {...commonCartesianGridProps} />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => formatTrendTick(value, trendWindow)}
                      interval={getTrendXAxisInterval(trendWindow)}
                      {...commonXAxisProps}
                    />
                    <YAxis {...commonYAxisProps} />
                    <Tooltip 
                      contentStyle={chartTooltipStyle}
                      labelFormatter={(value) => formatTrendTooltipLabel(value, trendWindow)}
                    />
                    <Area
                      type="monotone"
                      dataKey="conversations"
                      stroke="#6366f1"
                      fill="#6366f1"
                      fillOpacity={0.1}
                      strokeWidth={2}
                      name="Conversations"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="geospatial">
          <RegionalMapView
            title="Conversation Map"
            description="Conversation activity by region"
            regions={(mapRegionalBreakdown || []).map((region) => ({
              region: region.region,
              value: region.conversations,
              secondaryValue: region.active_users,
            }))}
            countries={(mapCountryBreakdown || []).map((country) => ({
              country: country.country,
              region: country.region,
              value: country.conversations,
              secondaryValue: country.active_users,
            }))}
            metricLabel="Conversations"
            secondaryLabel="Active users"
            colorScale="green"
            timeRangeValue={mapWindow}
            onTimeRangeChange={setMapWindow}
            loading={mapLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
